import { capitalize } from '@material-ui/core'
import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import FImage from 'components/BaseComponents/FImage'
import FStaticTable from 'components/BaseComponents/FStaticTable'
import { desktopThreshold } from 'components/BaseComponents/constants'
import SBox from 'components/opinionless/SBox'
import SInputError from 'components/opinionless/SInputErrorV2'
import SText from 'components/opinionless/SText'
import { LBButton, LBText } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { useTranslation } from 'i18n'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { StepWizardChildProps } from 'react-step-wizard'
import { SStyled, useSTheme } from 'styles/theme'
import { formattedDateMMDDYYYY, getAge } from 'utils/date'
import { ga4_useTitle } from 'utils/ga4'
import { formatMoney } from 'utils/money'
import XBanner from 'v2/components/XBanner'
import { ApplicationContext } from 'v2/context/application'
import { useApplicationAnswers } from 'v2/hooks/useApplicationAnswers'
import { useApplicationBasicInfo } from 'v2/hooks/useApplicationBasicInfo'
import { useHouseholdMembers } from 'v2/hooks/useHouseholdMembers'
import ConditionalRender from '../../../../utils/conditionalRender'
import style from './style.module.scss'

import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'
import { HOUSEHOLD_MEMBER_RELATIONSHIP } from 'containers/Enrollment/HouseholdIncome/HouseholdIncomeMembersDOB'

const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`
const LBTextHeader = SStyled(SText)`
    ${(p) => `

        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            font-size: 24px;
            line-height: 130%;
        }
    `}
`

interface Props {
    setCurrentStep?: (number) => void
    applicationId: any
    personId: string
    fontFamily: any
    onNextStep?: any
    onPrevious?: any
    updatePercentage: any
}

const HouseholdIncomeReview: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)
    const applicationContext = useContext(ApplicationContext)

    const {
        loading: householdMembersLoading,
        error: householdMembersError,
        refetch: householdMembersRefetch,
    } = useHouseholdMembers()
    useApplicationBasicInfo()
    const {
        loading: applicationAnswersLoading,
        refetch: refetchApplicationAnswers,
        getAnswersByCategory,
    } = useApplicationAnswers()

    useEffect(() => {
        refetchApplicationAnswers()
    }, [refetchApplicationAnswers])
    useEffect(() => {
        householdMembersRefetch()
    }, [householdMembersRefetch])

    const [householdMembersDetails, setHouseholdMembersDetails] = useState([])

    const [loading, setLoading] = useState(true)
    const [isMobile, setIsMobile] = useState<boolean>(false)

    const { handleSubmit, errors } = useForm()

    ga4_useTitle('Household Income Review')

    const fontFamily = props.fontFamily

    useEffect(() => {
        if (!householdMembersLoading) {
            setLoading(false)
            setHouseholdMembersDetails(
                applicationContext.householdMembers.map((member) => ({
                    id: member.id,
                    name: member.name,
                    relation: member.relation,
                    dob: member.dob,
                    blindDisabled: member.blindDisabled,
                    earnedIncome: member.earnedIncome,
                    unearnedIncome: member.unearnedIncome,
                }))
            )
        }
    }, [applicationContext.householdMembers])

    const staticTable = useMemo(() => {
        if (applicationContext?.answers && applicationContext?.profile) {
            const earnedIncomeApplicant = applicationContext?.profile?.earnedIncome
            const unearnedIncomeApplicant = applicationContext?.profile?.unearnedIncome

            return [
                {
                    name: `${applicationContext?.profile?.firstName} ${applicationContext?.profile?.lastName}`,
                    relationship: t('ssi_benefits_wizard_relationship_self'),
                    age: getAge(applicationContext?.profile?.dob),
                    status: applicationContext?.profile?.blindDisabled
                        ? capitalize(t('ssi_benefits_wizard_blind_disabled_yes').toLowerCase())
                        : capitalize(t('ssi_benefits_wizard_blind_disabled_no').toLowerCase()),
                    earned_income: earnedIncomeApplicant ? `$${earnedIncomeApplicant}` : '-',
                    unearned_income: unearnedIncomeApplicant ? `$${unearnedIncomeApplicant}` : '-',
                },
                ...applicationContext?.householdMembers.map((member) => {
                    return {
                        name: member.name,
                        relationship: t(member.relation),
                        age: getAge(member.dob),
                        status: member.blindDisabled
                            ? capitalize(t('ssi_benefits_wizard_blind_disabled_yes').toLowerCase())
                            : capitalize(t('ssi_benefits_wizard_blind_disabled_no').toLowerCase()),
                        earned_income: member.earnedIncome ? `$${member.earnedIncome}` : '-',
                        unearned_income: member.unearnedIncome ? `$${member.unearnedIncome}` : '-',
                    }
                }),
            ]
        }

        return []
    }, [applicationContext.householdMembers])

    const generateHouseHoldInfoTableDatasource = () => {
        const tableData = []
        staticTable.forEach((element) => {
            const el = {}
            el[`${t('ssi_benefits_summary_table_key_name')}`] = element['name']
            el[`${t('ssi_benefits_summary_table_key_relationship')}`] = element['relationship']
            el[`${t('ssi_benefits_summary_table_key_age')}`] = element['age']
            el[`${t('ssi_benefits_summary_table_key_status')}`] = element['status']
            el[`${t('ssi_benefits_summary_table_key_earned_income')}`] = element['earned_income']
            el[`${t('ssi_benefits_summary_table_key_unearned_income')}`] = element['unearned_income']

            tableData.push(el)
        })
        return tableData
    }

    const onResize = () => {
        setIsMobile(window.innerWidth < desktopThreshold)
    }

    useEffect(() => {
        onResize()
        window.removeEventListener('resize', onResize)
        window.addEventListener('resize', onResize)
    }, [])

    const onSubmit = (values) => {
        props.onNextStep()
    }

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.householdInfo,
        WIZARD_STEPS_MAPPING.householdInfo.review,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    if (loading) {
        return null
    }
    return (
        <Container>
            <SBox as="form" onSubmit={handleSubmit(onSubmit)}>
                <FButtonPrevious onClick={props.previousStep} />
                <LBTextHeader
                    sx={{
                        textAlign: 'left',
                        fontSize: '32px',
                        color: theme.colors.secondaryColor,
                        lineHeight: '120%',
                        fontFamily: fontFamily.header,
                    }}
                >
                    {t('long_beach_household_income_review_header')}
                </LBTextHeader>
                <LBText
                    sx={{
                        textAlignLast: 'left',
                        whiteSpace: 'pre-line',
                        fontFamily: fontFamily.body,
                        fontWeight: '800',
                        lineHeight: '140%',
                        color: '#202020',
                        fontSize: '16px',
                        marginBottom: '24px',
                    }}
                >
                    {t('long_beach_household_income_review_subheader')}
                </LBText>

                <XBanner
                    title={t('long_beach_household_income_review_banner_title')}
                    description={t('long_beach_household_income_review_banner_description')}
                    isButton={false}
                    type="warning"
                />
                <ConditionalRender if={!isMobile}>
                    <div style={{ marginTop: '24px' }}>
                        <FStaticTable
                            tableData={generateHouseHoldInfoTableDatasource()}
                            headerColor={theme.colors.gray100}
                        />
                    </div>
                </ConditionalRender>
                <ConditionalRender if={isMobile}>
                    <div className={style.household_applicant_review_container}>
                        <div className={style.household_applicant_review_container__icon}>
                            <FImage imageUrl={'/lb_household_income_person.svg'} alt="person" />
                        </div>
                        <div className={style.household_applicant_review_container__applicant_group}>
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '700',
                                    lineHeight: '140%',
                                    color: theme.colors.lbBlue,
                                    fontSize: '14px',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t('long_beach_applicant_name_label_relationship', {
                                        name: `${applicationContext.profile?.firstName} ${applicationContext?.profile?.lastName}`,
                                        relationship: t('long_beach_household_applicant_label'),
                                    }),
                                }}
                            />
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '400',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '0px',
                                    marginTop: '5px !important',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: `${t('dob_placeholder')}: ${formattedDateMMDDYYYY(
                                        applicationContext?.profile?.dob
                                    )} (${t('ssi_benefits_summary_table_key_age')} ${getAge(
                                        applicationContext?.profile?.dob
                                    )})`,
                                }}
                            />
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '400',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '0px',
                                    marginTop: '0px !important',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: t(
                                        `${
                                            applicationContext?.profile?.blindDisabled
                                                ? 'ssi_benefits_wizard_blind_disabled_yes'
                                                : 'ssi_benefits_wizard_blind_disabled_no'
                                        }`
                                    ),
                                }}
                            />
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '400',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '0px',
                                    marginTop: '0px !important',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html:
                                        applicationContext?.profile?.earnedIncome !== '0' &&
                                        applicationContext?.profile.unearnedIncome !== '0'
                                            ? t('lb_household_income_review_income_earner_label')
                                            : null,
                                }}
                            />
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '400',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '0px',
                                    marginTop: '0px !important',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: `${t('ssi_benefits_summary_table_key_earned_income')}: ${formatMoney(
                                        applicationContext?.profile?.earnedIncome
                                    )}`,
                                }}
                            />
                            <LBText
                                sx={{
                                    textAlignLast: 'left',
                                    whiteSpace: 'pre-line',
                                    fontFamily: fontFamily.body,
                                    fontWeight: '400',
                                    lineHeight: '140%',
                                    color: theme.colors.gray400,
                                    fontSize: '14px',
                                    marginBottom: '0px',
                                    marginTop: '0px !important',
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: `${t('ssi_benefits_summary_table_key_unearned_income')}: ${formatMoney(
                                        applicationContext?.profile?.unearnedIncome
                                    )}`,
                                }}
                            />
                        </div>
                    </div>
                    {householdMembersDetails.map((member, index) => (
                        <div className={style.household_member_review_container}>
                            <div className={style.household_applicant_member_container__icon}>
                                <FImage imageUrl={'/lb_household_income_person_bw.svg'} alt="person" />
                            </div>
                            <div className={style.household_member_review_container__member_group}>
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '700',
                                        lineHeight: '140%',
                                        color: theme.colors.gray400,
                                        fontSize: '14px',
                                        marginBottom: '5px',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: t('long_beach_applicant_name_label_relationship', {
                                            name: `${member?.name}`,
                                            relationship: t(`${HOUSEHOLD_MEMBER_RELATIONSHIP[member?.relation]}`),
                                        }),
                                    }}
                                />
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '400',
                                        lineHeight: '140%',
                                        color: theme.colors.gray400,
                                        fontSize: '14px',
                                        marginBottom: '0px',
                                        marginTop: '5px !important',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: `${t('dob_placeholder')}: ${formattedDateMMDDYYYY(member?.dob)} (${t(
                                            'ssi_benefits_summary_table_key_age'
                                        )} ${getAge(member?.dob)})`,
                                    }}
                                />
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '400',
                                        lineHeight: '140%',
                                        color: theme.colors.gray400,
                                        fontSize: '14px',
                                        marginBottom: '0px',
                                        marginTop: '0px !important',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: t(
                                            `${
                                                member?.blindDisabled
                                                    ? 'ssi_benefits_wizard_blind_disabled_yes'
                                                    : 'ssi_benefits_wizard_blind_disabled_no'
                                            }`
                                        ),
                                    }}
                                />
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '400',
                                        lineHeight: '140%',
                                        color: theme.colors.gray400,
                                        fontSize: '14px',
                                        marginBottom: '0px',
                                        marginTop: '0px !important',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            member?.earnedIncome !== '0' && member?.unearnedIncome !== '0'
                                                ? t('lb_household_income_review_income_earner_label')
                                                : null,
                                    }}
                                />
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '400',
                                        lineHeight: '140%',
                                        color: theme.colors.gray400,
                                        fontSize: '14px',
                                        marginBottom: '0px',
                                        marginTop: '0px !important',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: `${t('ssi_benefits_summary_table_key_earned_income')}: ${formatMoney(
                                            member?.earnedIncome
                                        )}`,
                                    }}
                                />
                                <LBText
                                    sx={{
                                        textAlignLast: 'left',
                                        whiteSpace: 'pre-line',
                                        fontFamily: fontFamily.body,
                                        fontWeight: '400',
                                        lineHeight: '140%',
                                        color: theme.colors.gray400,
                                        fontSize: '14px',
                                        marginBottom: '0px',
                                        marginTop: '0px !important',
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: `${t('ssi_benefits_summary_table_key_unearned_income')}: ${formatMoney(
                                            member?.unearnedIncome
                                        )}`,
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </ConditionalRender>

                <LBButton
                    type={'submit'}
                    id={'continueButton'}
                    loading={loading}
                    title={t('continue')}
                    width={'100%'}
                    mt={7}
                    mb={5}
                    textStyle={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '140%',
                        fontFamily: fontFamily.body,
                        color: theme.colors.white,
                    }}
                >
                    {t('continue')}
                </LBButton>
                {errors.lbHouseholdreview && (
                    <SInputError
                        sx={{
                            fontFamily: fontFamily.body,
                            color: theme.colors.redError,
                        }}
                        forElementId={'continueButton'}
                    >
                        {t(errors.lbHouseholdReview?.message)}
                    </SInputError>
                )}
            </SBox>
        </Container>
    )
}

export default HouseholdIncomeReview
